import { useState, useEffect } from 'react';
import { Search, X, Check, ChevronRight } from 'lucide-react';
import apiService from '../../services/apiService';

function monthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 23; i >= -12; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
    opts.push({ value, label });
  }
  return opts;
}

const TODAY = new Date().toISOString().slice(0, 10);
const CURRENT_MONTH = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
const PAYMENT_METHODS = ['Cash', 'Bank Transfer', 'Standing Order', 'Direct Debit'];

export default function AddContributionModal({ onClose, onSaved, initialMember = null, initialMonth = null }) {
  const [search, setSearch]               = useState('');
  const [results, setResults]             = useState([]);
  const [searching, setSearching]         = useState(false);
  const [selected, setSelected]           = useState(initialMember || null);
  const [amount, setAmount]               = useState(initialMember ? String(initialMember.subscriptionAmount || initialMember.contribution || '') : '');
  const [startMonth, setStartMonth]       = useState(initialMonth || CURRENT_MONTH);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [paidDate, setPaidDate]           = useState(TODAY);
  const [reference, setReference]         = useState('');
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState('');
  const [covered, setCovered]             = useState(null);

  useEffect(() => {
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const members = await apiService.getMembers({ status: 'active', search: search || '' });
        setResults(members.slice(0, 8));
      } finally {
        setSearching(false);
      }
    }, search.length === 0 ? 0 : 300);
    return () => clearTimeout(t);
  }, [search]);

  const handleSelect = (m) => {
    setSelected(m);
    setResults([]);
    setAmount(String(m.subscriptionAmount || m.contribution || ''));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      const result = await apiService.createContribution({
        memberId: selected.id,
        amount: parseFloat(amount),
        startMonth,
        paymentMethod,
        paidDate,
        notes: reference || null,
      });
      setCovered(result.covered || []);
    } catch (err) {
      setError(err.message || 'Failed to save contribution');
      setSaving(false);
    }
  };

  // Approximate preview: how many months will this cover?
  const obligation = selected ? (parseFloat(selected.subscriptionAmount || selected.contribution) || 0) : 0;
  const parsedAmount = parseFloat(amount) || 0;
  const estMonths = obligation > 0 ? Math.floor(parsedAmount / obligation) : 0;
  const hasRemainder = obligation > 0 && parsedAmount % obligation > 0;

  const familyCount = selected ? ((selected.family || []).filter(Boolean).length + 1) : 0;
  const months = monthOptions();

  const fmtMonth = (m) => {
    const d = new Date(m);
    return d.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">Add Contribution</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success screen */}
        {covered && (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3 text-emerald-700">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Check className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold">{covered.length} month{covered.length !== 1 ? 's' : ''} covered</p>
                <p className="text-sm text-slate-500">{selected?.name}</p>
              </div>
            </div>
            <div className="space-y-1.5">
              {covered.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-sm px-3 py-2 bg-slate-50 rounded-lg">
                  <span className="text-slate-700">{fmtMonth(c.month)}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">£{Number(c.amount).toFixed(2)}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {c.status === 'paid' ? 'Paid' : 'Partial'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => onSaved()}
              className="w-full px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-xl transition-colors"
            >
              Done
            </button>
          </div>
        )}

        {!covered && <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Member search */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Account Holder <span className="text-red-500">*</span>
            </label>
            {selected ? (
              <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                <div>
                  <p className="font-medium text-slate-900 text-sm">{selected.name}</p>
                  <p className="text-xs text-slate-500">{selected.membershipRef} · {familyCount} member{familyCount !== 1 ? 's' : ''}</p>
                </div>
                <button type="button" onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-600 ml-3">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name, ref, phone or postcode…"
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  autoFocus
                />
                {!selected && (results.length > 0 || searching) && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-10 max-h-52 overflow-y-auto">
                    {searching && results.length === 0 && <p className="px-4 py-3 text-sm text-slate-400">Searching…</p>}
                    {results.map(m => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => handleSelect(m)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-left border-b border-slate-50 last:border-0"
                      >
                        <div>
                          <p className="text-sm font-medium text-slate-900">{m.name}</p>
                          <p className="text-xs text-slate-500">{m.membershipRef} · {((m.family || []).filter(Boolean).length + 1)} members</p>
                        </div>
                        <span className="text-sm font-semibold text-emerald-600">£{m.subscriptionAmount || m.contribution}/mo</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Amount (£) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              required
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          {/* Starting Month */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Starting Month <span className="text-red-500">*</span>
            </label>
            <select
              value={startMonth}
              onChange={e => setStartMonth(e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
            >
              {months.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Allocation preview */}
          {selected && parsedAmount > 0 && obligation > 0 && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-xl text-sm text-blue-700">
              <ChevronRight className="w-4 h-4 flex-shrink-0" />
              {estMonths > 0
                ? <>Covers approx. <strong className="mx-1">{estMonths} month{estMonths !== 1 ? 's' : ''}</strong> {hasRemainder ? `+ £${(parsedAmount - estMonths * obligation).toFixed(2)} partial` : ''}</>
                : <>Partial payment — less than one month's obligation of £{obligation.toFixed(2)}</>
              }
            </div>
          )}

          {/* Method + Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Payment Method <span className="text-red-500">*</span>
              </label>
              <select
                value={paymentMethod}
                onChange={e => setPaymentMethod(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
              >
                {PAYMENT_METHODS.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Payment Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={paidDate}
                onChange={e => setPaidDate(e.target.value)}
                required
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          {/* Reference */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Reference <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={reference}
              onChange={e => setReference(e.target.value)}
              placeholder="e.g. cheque number, bank ref…"
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selected || !amount || saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-xl transition-colors"
            >
              {saving ? 'Saving…' : <><Check className="w-4 h-4" /> Add Contribution</>}
            </button>
          </div>
        </form>}
      </div>
    </div>
  );
}
